import { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Calendar, MapPin, ArrowLeft, FileText, Download, 
  Mail, MessageSquare, Music, ExternalLink, Upload, AlertTriangle, Check
} from 'lucide-react';
import { toast } from 'sonner';
import MusicSubmissionForm from './Public/MusicSubmissionForm';

export default function PublicCompetitionPage() {
  const { pageId } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Inquiry form state
  const [inquiryName, setInquiryName] = useState('');
  const [inquiryEmail, setInquiryEmail] = useState('');
  const [inquiryMessage, setInquiryMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [inquirySubmittedEmail, setInquirySubmittedEmail] = useState('');
  
  // Upload entries state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const fileInputRef = useRef(null);
  
  // Check if entry uploads are enabled. Driven by the per-page
  // ``uploads_enabled`` flag (managed via the Competition Page editor)
  // — the registration deadline is now informational only and no longer
  // auto-hides the Upload Entries tile.
  const isUploadsEnabled = () => {
    return pageData?.page?.uploads_enabled !== false;
  };
  
  useEffect(() => {
    axios.get(`${API}/public/competition/${pageId}`)
      .then(res => {
        setPageData(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError('Competition page not found or not yet published.');
        setLoading(false);
      });
  }, [pageId]);
  
  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return 'TBA';
    try {
      const start = new Date(startDate);
      if (endDate) {
        const end = new Date(endDate);
        const sameDay = start.getDate() === end.getDate() && start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
        if (sameDay) {
          return start.toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' });
        }
        if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
          return `${start.getDate()}-${end.getDate()} ${start.toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' })}`;
        }
        return `${start.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}`;
      }
      return start.toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return startDate;
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    try {
      return new Date(dateStr).toLocaleDateString('en-NZ', { 
        day: 'numeric', month: 'long', year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };
  
  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    if (!inquiryName || !inquiryEmail || !inquiryMessage) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/competition/${pageId}/inquiry`, {
        competition_id: pageData.competition.competition_id,
        name: inquiryName,
        email: inquiryEmail,
        message: inquiryMessage
      });
      setInquirySubmittedEmail(inquiryEmail);
      toast.success('Your inquiry has been submitted!');
      setInquiryName('');
      setInquiryEmail('');
      setInquiryMessage('');
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleUploadEntries = async () => {
    if (!uploadFile) {
      toast.error('Please select a file to upload');
      return;
    }
    
    setUploading(true);
    setUploadResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const response = await axios.post(
        `${API}/public/competition/${competition.competition_id}/import-entries`,
        formData,
        { 
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      setUploadResults(response.data);
      
      if (response.data.success_count > 0) {
        toast.success(`Uploaded ${response.data.success_count} entries successfully`);
      }
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} rows had errors - see details below`);
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };
  
  const downloadErrorReport = () => {
    if (!uploadResults?.error_report_url) return;
    
    const link = document.createElement('a');
    link.href = uploadResults.error_report_url;
    link.download = 'Import_Errors.xlsx';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };
  
  const resetUploadDialog = () => {
    setUploadDialogOpen(false);
    setUploadFile(null);
    setUploadResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const getDocumentTypeLabel = (type) => {
    const labels = {
      'event_notice': 'Event Notice',
      'event_programme': 'Event Programme',
      'order_of_skate': 'Order of Skate',
      'programme_print': 'Programme (Print Friendly)',
      'programme_screen': 'Programme (Screen Friendly)',
      'figure_draw': 'Figure Draw'
    };
    return labels[type] || type;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading competition details...</p>
        </div>
      </div>
    );
  }
  
  if (error || !pageData) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
        <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-200">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-[#2D4A3E]">CompCreator</Link>
            <Link to="/coming-competitions">
              <Button variant="ghost">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Competitions
              </Button>
            </Link>
          </div>
        </nav>
        <div className="max-w-7xl mx-auto px-6 py-16 text-center">
          <h2 className="text-2xl font-bold text-stone-700 mb-4">Page Not Found</h2>
          <p className="text-muted-foreground">{error}</p>
          <Link to="/coming-competitions">
            <Button className="mt-6 btn-primary">View All Competitions</Button>
          </Link>
        </div>
      </div>
    );
  }
  
  const { page, competition, host_organisation, venue, published_documents } = pageData;
  const hasDocuments = published_documents && published_documents.length > 0;
  
  // Generate default intro if no custom content provided. Custom content
  // is stored as HTML from the rich-text editor and rendered directly so
  // bold/italic/list formatting is preserved.
  const customIntroHtml = (() => {
    if (page?.content && page.content.replace(/<[^>]*>/g, '').trim()) {
      return page.content;
    }
    return null;
  })();

  const defaultIntroText = (() => {
    let intro = `${host_organisation?.name || 'The host organisation'} presents the ${competition.year} ${competition.name}`;
    if (competition.sub_competition_name) {
      intro += `, running side by side with the ${competition.year} ${competition.sub_competition_name}`;
    }
    intro += '.';
    return intro;
  })();
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-[#2D4A3E] hover:opacity-80">
            CompCreator
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/coming-competitions">
              <Button variant="ghost" data-testid="back-button">
                <ArrowLeft className="mr-2 h-4 w-4" />
                All Competitions
              </Button>
            </Link>
            <Link to="/login">
              <Button className="btn-primary" data-testid="login-button">Login</Button>
            </Link>
          </div>
        </div>
      </nav>
      
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-[#2D4A3E] to-[#4A6B4A] text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" data-testid="competition-title">
            {page?.title || `The ${competition.year} ${competition.name} is on the way!`}
          </h1>
          
          {/* Disciplines */}
          <div className="flex gap-3 mt-4">
            {competition.artistic_events_included && (
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                Artistic
              </span>
            )}
            {competition.figure_events_included && (
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                Figures
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Intro Paragraph */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {customIntroHtml ? (
          <div
            className="text-lg text-stone-700 prose prose-stone max-w-none"
            data-testid="competition-intro"
            dangerouslySetInnerHTML={{ __html: customIntroHtml }}
          />
        ) : (
          <p className="text-lg text-stone-700" data-testid="competition-intro">
            {defaultIntroText}
          </p>
        )}
      </div>
      
      {/* Main Content - Two Column Layout */}
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column - When and Where + Music + Questions */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-6" data-testid="when-where-section">
              <h2 className="text-2xl font-bold text-[#2D4A3E] mb-6 flex items-center gap-2">
                <Calendar className="h-6 w-6" />
                When and Where?
              </h2>
              
              {/* Venue */}
              {venue && (
                <div className="mb-6">
                  <h3 className="font-semibold text-lg text-stone-800 mb-2">Venue</h3>
                  <p className="text-stone-700 font-medium">{venue.venue_name}</p>
                  <p className="text-stone-600">
                    {venue.street && <>{venue.street}<br/></>}
                    {venue.suburb && <>{venue.suburb}<br/></>}
                    {venue.city} {venue.postcode}
                  </p>
                  {venue.google_maps_url && (
                    <a 
                      href={venue.google_maps_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-green-700 hover:text-green-800 mt-2 font-medium"
                    >
                      <MapPin className="h-4 w-4" />
                      View on Google Maps
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              )}
              
              {/* Dates */}
              <div className="mb-6">
                <h3 className="font-semibold text-lg text-stone-800 mb-2">Competition Dates</h3>
                <p className="text-stone-700 text-lg">
                  {formatDateRange(competition.start_date, competition.end_date)}
                </p>
              </div>
              
              {/* Close of Entries */}
              {competition.registration_deadline && (
                <div className="p-4 bg-amber-50 border-l-4 border-amber-500 rounded">
                  <h3 className="font-semibold text-amber-800 mb-1">Close of Entries</h3>
                  <p className="text-amber-700 font-medium">
                    {formatDate(competition.registration_deadline)}
                  </p>
                </div>
              )}

              {/* Music and Content Sheets deadline */}
              {competition.music_submission_deadline && (
                <div className="p-4 mt-2 bg-amber-50 border-l-4 border-amber-500 rounded" data-testid="music-deadline-banner">
                  <h3 className="font-semibold text-amber-800 mb-1">Music and Content Sheets must be in by</h3>
                  <p className="text-amber-700 font-medium">
                    {formatDate(competition.music_submission_deadline)}
                  </p>
                </div>
              )}
            </Card>
            
            {/* Music & Content Sheet Submission */}
            <Card className="p-6" data-testid="music-section">
              <h2 className="text-2xl font-bold text-[#2D4A3E] mb-4 flex items-center gap-2">
                <Music className="h-6 w-6" />
                Music and Content Sheet Submission
              </h2>
              {page.music_submissions_enabled === false ? (
                <div className="p-4 bg-stone-50 border border-stone-200 rounded-lg" data-testid="music-submissions-closed">
                  <p className="font-semibold text-stone-800 mb-1">
                    Music and content sheet submissions are now closed
                  </p>
                  <p className="text-sm text-stone-600">
                    If you have music or content sheets still to send — or to update — please
                    contact the competition via your club secretary.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-stone-600 mb-4">
                    Upload your competition music files and content sheets here. Please submit
                    before the deadline. Files are renamed automatically to match the
                    competition's naming convention.
                  </p>
                  <MusicSubmissionForm pageId={pageId} />
                </>
              )}
            </Card>
            
            {/* Questions/Comments Form — hidden when inquiry_form_enabled === false */}
            {page.inquiry_form_enabled !== false && (
            <Card className="p-6" data-testid="inquiry-section">
              <h2 className="text-2xl font-bold text-[#2D4A3E] mb-4 flex items-center gap-2">
                <MessageSquare className="h-6 w-6" />
                Questions or Comments?
              </h2>

              {inquirySubmittedEmail ? (
                <div
                  className="rounded-lg border border-green-200 bg-green-50 p-5"
                  data-testid="inquiry-acknowledgement"
                >
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-green-100 p-2 shrink-0">
                      <Check className="h-5 w-5 text-green-700" />
                    </div>
                    <div className="space-y-2">
                      <p className="font-semibold text-green-900 text-lg">
                        Thanks — your message is on its way!
                      </p>
                      <p className="text-sm text-stone-700">
                        We've also dropped a copy of your inquiry into{' '}
                        <strong>{inquirySubmittedEmail}</strong> so you have a record
                        of what you sent. If it hasn't arrived within a few minutes,
                        check your spam folder.
                      </p>
                      <p className="text-sm text-stone-700">
                        Someone from the host organisation will reply to you directly.
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setInquirySubmittedEmail('')}
                    data-testid="inquiry-send-another"
                  >
                    Send another message
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-stone-600 mb-6">
                    Have a question about this competition? Send us a message and we'll get back to you.
                    A copy will also be sent to your inbox so you have a record of what was asked.
                  </p>
                  <form onSubmit={handleInquirySubmit} className="space-y-4">
                <div>
                  <Label htmlFor="inquiry-name">Name *</Label>
                  <Input
                    id="inquiry-name"
                    type="text"
                    placeholder="Your full name"
                    value={inquiryName}
                    onChange={(e) => setInquiryName(e.target.value)}
                    required
                    data-testid="inquiry-name"
                  />
                </div>
                <div>
                  <Label htmlFor="inquiry-email">Email Address *</Label>
                  <Input 
                    id="inquiry-email"
                    type="email" 
                    placeholder="your@email.com"
                    value={inquiryEmail}
                    onChange={(e) => setInquiryEmail(e.target.value)}
                    required
                    data-testid="inquiry-email"
                  />
                </div>
                <div>
                  <Label htmlFor="inquiry-message">Your Message *</Label>
                  <textarea 
                    id="inquiry-message"
                    className="w-full min-h-[120px] px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Type your question or comment here..."
                    value={inquiryMessage}
                    onChange={(e) => setInquiryMessage(e.target.value)}
                    required
                    data-testid="inquiry-message"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="btn-primary"
                  disabled={submitting}
                  data-testid="submit-inquiry"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  {submitting ? 'Sending...' : 'Send Inquiry'}
                </Button>
              </form>
                </>
              )}
            </Card>
            )}
          </div>
          
          {/* Right Column - Contact at top, then Documents */}
          <div className="space-y-6">
            {/* Contact Info - Now at TOP of right column */}
            {host_organisation?.contact_email && (
              <Card className="p-6" data-testid="contact-section">
                <h3 className="font-semibold text-[#2D4A3E] mb-3 flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Contact
                </h3>
                <p className="text-sm text-stone-600 mb-2">{host_organisation.name}</p>
                <a 
                  href={`mailto:${host_organisation.contact_email}`}
                  className="text-green-700 hover:text-green-800 text-sm flex items-center gap-1"
                >
                  <Mail className="h-4 w-4" />
                  {host_organisation.contact_email}
                </a>
              </Card>
            )}
            
            {/* Competition Documents - Only shown if documents exist */}
            {hasDocuments && (
              <Card className="p-6" data-testid="documents-section">
                <h2 className="text-xl font-bold text-[#2D4A3E] mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Competition Documents
                </h2>
                
                <div className="space-y-3">
                  {published_documents.map(doc => (
                    <a
                      key={doc.document_id}
                      href={`${API}/public/competition/${pageId}/document/${doc.document_id}`}
                      className="block p-4 bg-stone-50 hover:bg-stone-100 rounded-lg border border-stone-200 transition-colors"
                      data-testid={`document-${doc.document_type}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded">
                          <FileText className="h-5 w-5 text-green-700" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-stone-800">
                            {getDocumentTypeLabel(doc.document_type)}
                          </p>
                          <p className="text-xs text-stone-500">PDF Document</p>
                        </div>
                        <Download className="h-4 w-4 text-stone-400" />
                      </div>
                    </a>
                  ))}
                </div>
              </Card>
            )}
            
            {/* Upload Entries Tile — controlled by the page's
                ``uploads_enabled`` flag. When disabled the user sees an
                "Entries are now closed" notice instead. */}
            {isUploadsEnabled() ? (
              <Card className="p-6" data-testid="upload-entries-section">
                <h3 className="font-semibold text-[#2D4A3E] mb-3 flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Upload Entries
                </h3>
                <p className="text-sm text-stone-600 mb-4">
                  Submit your club's entries using the entry form template.
                </p>
                <Button 
                  onClick={() => setUploadDialogOpen(true)}
                  className="w-full btn-primary"
                  data-testid="upload-entries-btn"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Entry Form
                </Button>
              </Card>
            ) : (
              <Card
                className="p-4 bg-amber-50 border-l-4 border-amber-500 rounded"
                data-testid="entries-closed-section"
              >
                <h3 className="font-semibold text-amber-800 mb-1">Entries are now closed</h3>
                <p className="text-sm text-amber-700">
                  The entries are in and work on the programme is underway. If there are any
                  corrections to be made to entries already submitted, please contact the
                  competition via your club secretary.
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>
      
      {/* Upload Entries Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-green-700" />
              Upload Entries
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {!uploadResults ? (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <div className="flex gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Before uploading:</p>
                      <ul className="text-amber-700 mt-1 space-y-0.5 list-disc list-inside">
                        <li>Ensure you're using the official entry template</li>
                        <li>Rows with missing required data will be skipped</li>
                        <li>Duplicate entries will be reported but not created</li>
                        <li>You can download an error report after upload</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Entry File (Excel) *</Label>
                  <Input 
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    data-testid="upload-file-input"
                  />
                  {uploadFile && (
                    <p className="text-sm text-muted-foreground">
                      Selected: {uploadFile.name}
                    </p>
                  )}
                </div>
                
                <div className="flex justify-end gap-3 pt-2 border-t mt-4">
                  <Button 
                    variant="outline" 
                    onClick={resetUploadDialog}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleUploadEntries}
                    className="btn-primary"
                    disabled={!uploadFile || uploading}
                    data-testid="upload-confirm-btn"
                  >
                    {uploading ? (
                      <>Uploading...</>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Upload
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Upload Results */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-green-700">{uploadResults.success_count}</p>
                      <p className="text-sm text-green-600">Entries Created</p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-red-700">{uploadResults.error_count}</p>
                      <p className="text-sm text-red-600">Errors</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between p-2 bg-stone-50 rounded">
                      <span>New Skaters:</span>
                      <span className="font-medium">{uploadResults.created_skaters}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-stone-50 rounded">
                      <span>Updated Skaters:</span>
                      <span className="font-medium">{uploadResults.updated_skaters}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-stone-50 rounded col-span-2">
                      <span>Duplicates Skipped:</span>
                      <span className="font-medium">{uploadResults.duplicate_count}</span>
                    </div>
                  </div>
                  
                  {uploadResults.success_count > 0 && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-600" />
                      <p className="text-sm text-green-700">
                        Entries have been submitted successfully!
                      </p>
                    </div>
                  )}
                  
                  {uploadResults.error_report_url && (
                    <Button 
                      variant="outline" 
                      onClick={downloadErrorReport}
                      className="w-full border-red-300 text-red-700 hover:bg-red-50"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download Error Report
                    </Button>
                  )}
                  
                  <div className="flex justify-end pt-2 border-t">
                    <Button onClick={resetUploadDialog} className="btn-primary">
                      Done
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Footer */}
      <footer className="bg-[#2D4A3E] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-green-200 text-sm">
            © {new Date().getFullYear()} CompCreator. All rights reserved.
          </p>
          <p className="text-green-300 text-xs mt-2">
            Brought to you by CompCreator
          </p>
        </div>
      </footer>
    </div>
  );
}
